// Une absence tombe-t-elle sur une séance ? Miroir EXACT de server/utils/detectionAbsence.ts (parseTimeRange, heureVersMinutes,
// absenceRecoupeAtelier) : la détection Tipee n'ouvre un brouillon que si l'absence recoupe l'horaire de l'atelier, avec 1 h de
// battement — le calendrier ne compte donc « à couvrir » que ces séances-là (DECISIONS §12 et §25). Les deux fichiers DOIVENT rester
// identiques ; recoupement.test.ts reprend les cas du test serveur. Au doute (journée entière, horaire illisible), on prévient.

export interface PlageHoraire { debutMin: number; finMin: number }

/** Horaire d'une absence (`time_range` de Tipee) ; null = journée entière (ou illisible : au doute, on prévient). */
export function parseTimeRange(tr: string | null | undefined, dateISO?: string): PlageHoraire | null {
  if (!tr) return null;
  const t = tr.trim();
  // Un horaire avec FUSEAU (« …Z », « +02:00 ») n'est pas lisible sans risque de décalage de 1 à 2 h : journée entière.
  if (/\dZ\b/i.test(t) || /\+\d{2}:?\d{2}\b/.test(t) || /T[\d:.]+-\d{2}:?\d{2}\b/.test(t)) return null;

  const iso = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?\s*\/\s*(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(t);
  if (iso) {
    const [, d1, h1, m1, d2, h2, m2] = iso;
    const jour = dateISO ?? d1;
    if (jour < d1 || jour > d2) return null;
    const debutMin = jour === d1 ? Number(h1) * 60 + Number(m1) : 0;
    const finMin = jour === d2 ? Number(h2) * 60 + Number(m2) : 24 * 60;
    if (finMin <= debutMin) return null;
    if (debutMin === 0 && finMin >= 24 * 60 - 1) return null; // toute la journée
    return { debutMin, finMin };
  }
  // Autre texte daté (format inattendu) : journée entière.
  if (/\d{4}-\d{2}-\d{2}/.test(t)) return null;
  // Texte à plusieurs plages (« 10h-12h, 14h-16h ») : ne se réduit pas à une seule.
  const heures = [...t.matchAll(/(\d{1,2})\s*(?:h|:)\s*(\d{2})?(?::\d{2})?/gi)]
    .map((m) => Number(m[1]) * 60 + (m[2] ? Number(m[2]) : 0));
  if (heures.length !== 2) return null;
  const [debutMin, finMin] = heures;
  if (finMin <= debutMin) return null;
  return { debutMin, finMin };
}

/** « 14:00:00 » ou « 14:00 » (colonne TIME de Postgres) → minutes ; null si absent. */
export function heureVersMinutes(t: string | null | undefined): number | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/**
 * L'absence tombe-t-elle sur la séance, battement compris ? Vrai si la distance entre l'absence et la séance est STRICTEMENT
 * inférieure au battement (0 h d'écart = vrai ; exactement 1 h d'écart avec 1 h de battement = faux). Absence sans horaire, ou
 * séance sans horaire connu : vrai (au doute, on prévient).
 */
export function absenceRecoupeSeance(
  absence: PlageHoraire | null,
  seance: { heure_debut: string | null; heure_fin: string | null },
  battementMin: number,
): boolean {
  if (!absence) return true;
  const debut = heureVersMinutes(seance.heure_debut);
  const fin = heureVersMinutes(seance.heure_fin);
  if (debut == null || fin == null) return true;
  return absence.debutMin < fin + battementMin && absence.finMin > debut - battementMin;
}
